import type {
  Me, Recipe, RecipeDetail, CatalogRecipe, RecipeSearchResult, Meal, MealDetail, MealAdjustPayload, SlotKey, MealStatus, IngredientRow, IngredientList,
  PantryItem, LeftoverLot, ShoppingPayload, ShoppingItem, ReconcileRequired, ReconcilePayload,
  ChatConversation, ChatConversationDetail, ChatModelsResponse,
  SlotMask, SlotMaskCell, PlanWeekMode, PlanWeekResult, PlanWeekNeedsDisambiguation, PlanWeekNeedsDuplicateConfirm,
  Rule, RuleValidation, RuleKind, RuleAction,
  AccountSharing, AccountRole, InvitePreview,
} from './types'

// RuleValidationError is thrown by api.createRule / api.updateRule when the
// server rejects the DSL. Carries the {field, message} list so the editor can
// surface inline errors.
export class RuleValidationError extends Error {
  result: RuleValidation
  constructor(r: RuleValidation) {
    super(r.errors[0]?.message ?? 'rule validation failed')
    this.result = r
  }
}

// PlanWeekNeedsDisambiguationError is thrown by api.planWeek when the backend
// reports conflicting planned meals and the caller didn't specify a mode.
// Surface the envelope to the user (modal with Overwrite/Fill empty/Cancel)
// and re-call api.planWeek with an explicit mode.
export class PlanWeekNeedsDisambiguationError extends Error {
  envelope: PlanWeekNeedsDisambiguation
  constructor(env: PlanWeekNeedsDisambiguation) {
    super(env.message)
    this.envelope = env
  }
}

// PlanWeekNeedsDuplicateConfirmError is thrown by api.planWeek when at least one
// slot type has more days to fill than distinct eligible recipes, so the planner
// would repeat recipes within the week. Surface the envelope (a confirm dialog)
// and re-call api.planWeek with force=true once the user accepts duplicates.
export class PlanWeekNeedsDuplicateConfirmError extends Error {
  envelope: PlanWeekNeedsDuplicateConfirm
  constructor(env: PlanWeekNeedsDuplicateConfirm) {
    super(env.message)
    this.envelope = env
  }
}

// RuleWriteBody is the shape both createRule and updateRule accept. Mirrors
// the backend's ruleWritePayload — empty/undefined optional fields are
// dropped server-side, so the editor can send them as null/undefined.
export type RuleWriteBody = {
  name: string
  kind: RuleKind
  action?: RuleAction
  scope_expr: string
  predicate_expr: string
  multiplier_expr?: string
  effective_from?: string
  effective_through?: string
}

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'same-origin', ...init })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  if (r.status === 204) return undefined as T
  return r.json() as Promise<T>
}

// ReconcileRequiredError is thrown by setStatus when the backend asks the
// user to verify or fill in pantry stock before cooking can proceed.
export class ReconcileRequiredError extends Error {
  envelope: ReconcileRequired
  constructor(env: ReconcileRequired) {
    super('pantry_reconcile_required')
    this.envelope = env
  }
}

export const api = {
  updateMe: (body: {
    ui_scale?: number
    mass_units?: 'metric' | 'imperial'
    volume_units?: 'metric' | 'imperial'
    temp_units?: 'F' | 'C'
    dietary_restrictions?: string
    chat_model?: string
    chat_effort?: string
    tour_model?: string
    tour_effort?: string
    greeting_effort?: string
  }) =>
    jfetch<Me>('/api/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  credits: () => jfetch<{ balance: number; markup: number }>('/api/credits'),

  agentDocs: () =>
    jfetch<{ path: string; description: string; tags: string[] }[]>('/api/agent-docs'),
  agentDoc: (path: string) =>
    jfetch<{ path: string; description: string; tags: string[]; body: string }>(
      `/api/agent-docs/${path.split('/').map(encodeURIComponent).join('/')}`,
    ),

  recipes: () => jfetch<Recipe[]>('/api/recipes'),
  // Create a blank draft recipe and return its summary row; the caller then
  // navigates to the editor at /recipes/{id}/edit.
  createRecipe: () => jfetch<Recipe>('/api/recipes', { method: 'POST' }),
  recipe: (id: number) => jfetch<RecipeDetail>(`/api/recipes/${id}`),
  recipesSearch: (q: string) =>
    jfetch<RecipeSearchResult>(`/api/recipes/search?q=${encodeURIComponent(q)}`),
  catalog: (opts?: { sort?: 'rating' | 'recent' | 'name'; q?: string }) => {
    const params = new URLSearchParams()
    if (opts?.sort) params.set('sort', opts.sort)
    if (opts?.q) params.set('q', opts.q)
    const qs = params.toString()
    return jfetch<CatalogRecipe[]>(`/api/recipes/catalog${qs ? `?${qs}` : ''}`)
  },
  adopt: (id: number) => jfetch<{ id: number }>(`/api/recipes/${id}/adopt`, { method: 'POST' }),
  publishRecipe: (id: number, published: boolean) =>
    jfetch<void>(`/api/recipes/${id}/publish`, { method: published ? 'PUT' : 'DELETE' }),
  rateRecipe: (id: number, stars: number) =>
    jfetch<void>(`/api/recipes/${id}/rating`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stars }),
    }),
  unrateRecipe: (id: number) =>
    jfetch<void>(`/api/recipes/${id}/rating`, { method: 'DELETE' }),
  adoptIngredient: (id: number) =>
    jfetch<IngredientRow>(`/api/ingredients/${id}/adopt`, { method: 'POST' }),

  getIngredient: (id: number) =>
    jfetch<IngredientRow>(`/api/ingredients/${id}`),

  // `limit` is a "give me the whole list" escape hatch used by PantryTab. The
  // pickers leave it unset and get the default cap-5-per-group behavior.
  ingredients: (q: string, limit?: number) =>
    jfetch<IngredientList>(`/api/ingredients?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ''}`),

  createIngredient: (body: { name: string; default_unit: string; notes?: string }) =>
    jfetch<IngredientRow>(`/api/ingredients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  updateIngredient: (id: number, body: {
    name: string; default_unit: string; notes?: string;
    purchase_qty?: number | null; purchase_unit?: string | null;
    target_qty?: number | null;
    shelf_life?: number | null;
  }) =>
    jfetch<IngredientRow>(`/api/ingredients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  pantryItems: () => jfetch<PantryItem[]>('/api/pantry/items'),
  createPantryItem: (body: {
    ingredient_id?: number; recipe_id?: number; quantity: number; unit: string;
    purchased_at?: string; expires_at?: string; notes?: string;
  }) =>
    jfetch<PantryItem>('/api/pantry/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  updatePantryItem: (id: number, body: Partial<{
    quantity: number; unit: string; purchased_at: string;
    expires_at: string; notes: string;
  }>) =>
    jfetch<PantryItem>(`/api/pantry/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  deletePantryItem: (id: number) =>
    jfetch<void>(`/api/pantry/items/${id}`, { method: 'DELETE' }),

  shopping: () => jfetch<ShoppingPayload>('/api/shopping'),
  createShoppingItem: (body: {
    ingredient_id: number; quantity: number; unit: string; notes?: string;
  }) =>
    jfetch<ShoppingItem>('/api/shopping/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  updateShoppingItem: (id: number, body: Partial<{
    quantity: number; unit: string; notes: string;
  }>) =>
    jfetch<ShoppingItem>(`/api/shopping/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  deleteShoppingItem: (id: number) =>
    jfetch<void>(`/api/shopping/items/${id}`, { method: 'DELETE' }),

  deleteRecipe: (id: number) =>
    jfetch<void>(`/api/recipes/${id}`, { method: 'DELETE' }),

  setRecipeImage: (id: number, base64: string, mime_type: string) =>
    jfetch<Recipe>(`/api/recipes/${id}/image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64, mime_type }),
    }),

  deleteRecipeImage: (id: number) =>
    jfetch<void>(`/api/recipes/${id}/image`, { method: 'DELETE' }),

  updateRecipe: (id: number, body: {
    name: string
    prep_minutes: number
    servings: number
    yield_quantity: number
    yield_unit: string
    instructions: string
    ingredients: { ingredient_id: number; component_recipe_id?: number; quantity: number; unit: string; optional: boolean; prep?: string }[]
    tags: Record<string, string[]>
  }) =>
    jfetch<void>(`/api/recipes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  meals: (from: string, to: string) =>
    jfetch<Meal[]>(`/api/meals?from=${from}&to=${to}`),

  meal: (id: number) => jfetch<MealDetail>(`/api/meals/${id}`),

  setMealAdjustments: (id: number, body: MealAdjustPayload) =>
    jfetch<MealDetail>(`/api/meals/${id}/adjustments`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  revertMealAdjustments: (id: number) =>
    jfetch<MealDetail>(`/api/meals/${id}/adjustments`, { method: 'DELETE' }),

  setSlot: (date: string, slot: SlotKey, recipe_id: number | null, status: MealStatus = 'planned') =>
    jfetch<{ id: number } | void>(`/api/meals`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, slot, recipe_id, status }),
    }),

  // Meal-eligible recipe lots on hand, for the slot picker's leftovers section.
  leftovers: () => jfetch<LeftoverLot[]>('/api/pantry/leftovers'),

  // Schedule an on-hand recipe lot into a slot as a leftover meal.
  setSlotLeftover: (date: string, slot: SlotKey, pantry_item_id: number) =>
    jfetch<{ id: number } | void>(`/api/meals`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, slot, from_pantry_item_id: pantry_item_id, status: 'planned' }),
    }),

  setStatus: async (
    id: number, status: MealStatus,
    opts?: { force?: boolean; reconcile?: ReconcilePayload; leftover_servings?: number },
  ) => {
    const r = await fetch(`/api/meals/${id}/status`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(opts || {}) }),
    })
    if (r.status === 409) {
      const env = (await r.json()) as ReconcileRequired
      if (env && env.code === 'pantry_reconcile_required') {
        throw new ReconcileRequiredError(env)
      }
      throw new Error('409')
    }
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  },

  slotMask: () => jfetch<SlotMask>('/api/slot-mask'),
  setSlotMask: (cells: SlotMaskCell[]) =>
    jfetch<SlotMask>('/api/slot-mask', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cells }),
    }),

  // planWeek: omit mode and force on the first call. On 409 the promise rejects
  // with either a PlanWeekNeedsDisambiguationError (existing meals — re-call with
  // mode='overwrite_future' or 'fill_empty_only') or a
  // PlanWeekNeedsDuplicateConfirmError (too few recipes for a slot type — re-call
  // with force=true, keeping the chosen mode, once the user accepts duplicates).
  planWeek: async (week_start: string, mode?: PlanWeekMode, force?: boolean): Promise<PlanWeekResult> => {
    const body: { week_start: string; mode?: PlanWeekMode; force?: boolean } = { week_start }
    if (mode) body.mode = mode
    if (force) body.force = true
    const r = await fetch('/api/plan-week', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (r.status === 409) {
      const env = (await r.json()) as PlanWeekNeedsDisambiguation | PlanWeekNeedsDuplicateConfirm
      if (env && env.code === 'needs_disambiguation') {
        throw new PlanWeekNeedsDisambiguationError(env)
      }
      if (env && env.code === 'needs_duplicate_confirm') {
        throw new PlanWeekNeedsDuplicateConfirmError(env)
      }
      throw new Error('409')
    }
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
    return r.json() as Promise<PlanWeekResult>
  },

  rules: () => jfetch<Rule[]>('/api/rules'),

  // createRule and updateRule throw a RuleValidationError (carrying the
  // {field, message} envelope) on 400 so the editor can render inline errors.
  // Other failures throw a plain Error.
  createRule: async (body: RuleWriteBody): Promise<Rule> => {
    const r = await fetch('/api/rules', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (r.status === 400) {
      const vr = (await r.json()) as RuleValidation
      throw new RuleValidationError(vr)
    }
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
    return r.json() as Promise<Rule>
  },
  updateRule: async (id: number, body: RuleWriteBody): Promise<Rule> => {
    const r = await fetch(`/api/rules/${id}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (r.status === 400) {
      const vr = (await r.json()) as RuleValidation
      throw new RuleValidationError(vr)
    }
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
    return r.json() as Promise<Rule>
  },
  toggleRule: (id: number, enabled: boolean) =>
    jfetch<Rule>(`/api/rules/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),
  deleteRule: (id: number) =>
    jfetch<void>(`/api/rules/${id}`, { method: 'DELETE' }),
  // Dry-run validate. Used by the editor for live feedback as the user types.
  validateRule: (body: RuleWriteBody) =>
    jfetch<RuleValidation>('/api/rules/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  chatSessionStart: () =>
    jfetch<{ pending: boolean; unread_conversation_id: string | null }>(
      '/api/chat/session-start',
      { method: 'POST' },
    ),
  // Generate a fresh proactive greeting when the user opens the chat. Returns a
  // null conversation_id when there's nothing to greet about.
  chatStartGreeting: () =>
    jfetch<{ conversation_id: string | null; message_id?: string }>(
      '/api/chat/greeting',
      { method: 'POST' },
    ),
  chatModels: () => jfetch<ChatModelsResponse>('/api/chat/models'),
  chatConversations: () => jfetch<ChatConversation[]>('/api/chat/conversations'),
  chatNewConversation: (model?: string) =>
    jfetch<{ id: string; model: string }>('/api/chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
    }),
  chatConversation: (id: string) =>
    jfetch<ChatConversationDetail>(`/api/chat/conversations/${id}`),
  chatDeleteConversation: (id: string) =>
    jfetch<void>(`/api/chat/conversations/${id}`, { method: 'DELETE' }),
  chatMarkRead: (id: string, messageId: string) =>
    jfetch<void>(`/api/chat/conversations/${id}/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: messageId }),
    }),
  chatRenameConversation: (id: string, title: string) =>
    jfetch<void>(`/api/chat/conversations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }),
  chatPostMessage: (
    conversationId: string,
    content: string,
    clientMessageId: string,
    image?: { base64: string; mime_type: string },
  ) =>
    jfetch<{ user_message_id: string; assistant_message_id: string }>(
      `/api/chat/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, client_message_id: clientMessageId, image }),
      },
    ),
  chatCancel: (messageId: string) =>
    jfetch<void>(`/api/chat/messages/${messageId}/cancel`, { method: 'POST' }),
  chatRetry: (conversationId: string) =>
    jfetch<{ assistant_message_id: string }>(
      `/api/chat/conversations/${conversationId}/retry`,
      { method: 'POST' },
    ),
  chatToolResult: (messageId: string, body: { tool_call_id: string; result?: unknown; error?: string }) =>
    jfetch<{ ok: boolean }>(`/api/chat/messages/${messageId}/tool-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // ---- Account-aliasing / delegated access -------------------------------
  // The owner's own sharing state: who they've granted access + pending invites.
  account: () => jfetch<AccountSharing>('/api/account'),
  // Invite an email at a role. If they already have an account the grant is
  // created immediately ({granted:true}); otherwise a pending invite is returned.
  inviteToAccount: (email: string, role: AccountRole) =>
    jfetch<{ granted: boolean; token?: string; grantee_name?: string }>('/api/account/invites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    }),
  revokeAccountInvite: (id: number) =>
    jfetch<void>(`/api/account/invites/${id}`, { method: 'DELETE' }),
  revokeAccountGrant: (id: number) =>
    jfetch<void>(`/api/account/grants/${id}`, { method: 'DELETE' }),
  invitePreview: (token: string) =>
    jfetch<InvitePreview>(`/api/account/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) =>
    jfetch<{ owner_id: number; role: AccountRole }>(
      `/api/account/invite/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),
  // Switch the session into another account (user_id) or back home (null).
  actAs: (userId: number | null) =>
    jfetch<void>('/api/session/act-as', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    }),
}
